import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Badge } from './ui/badge'
import { Tabs as ShadcnTabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'

const tabsVariants = cva('min-w-0 w-full', {
  variants: {
    variant: {
      default: '',
      pills: '',
      underline: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const tabsListVariants = cva(
  'inline-flex min-w-0 flex-nowrap items-center justify-start overflow-x-auto overflow-y-hidden text-text-secondary [-webkit-overflow-scrolling:touch]',
  {
    variants: {
      variant: {
        default: 'gap-1 rounded-full border border-border-default bg-white p-1',
        pills: 'gap-2 rounded-none bg-transparent p-0',
        underline: 'gap-0 rounded-none bg-transparent p-0',
      },
      size: {
        default: 'h-auto',
        sm: 'h-auto',
        lg: 'h-auto',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const tabsTriggerVariants = cva(
  'inline-flex flex-shrink-0 items-center justify-center gap-0.5 text-text-secondary whitespace-nowrap text-sm font-normal ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative',
  {
    variants: {
      variant: {
        default:
          'rounded-full bg-transparent hover:bg-background-hover data-[state=active]:bg-button-primary-selected-segment data-[state=active]:text-text-primary data-[state=active]:font-medium',
        pills:
          'rounded-full border border-border-default bg-background-surface hover:bg-background-hover data-[state=active]:border-icon-primary data-[state=active]:bg-button-primary-selected-segment data-[state=active]:text-text-primary data-[state=active]:font-medium data-[state=active]:shadow-[0_0_0_2px_rgba(0,174,255,0.15)]',
        underline:
          'group h-16 flex-col items-center justify-end gap-0.5 rounded-none bg-transparent px-4 !pb-0 text-center font-normal data-[state=active]:text-text-primary',
      },
      size: {
        default: 'px-3 py-1.5 text-sm',
        sm: 'px-2 py-1 text-xs',
        lg: 'px-4 py-2 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface TabItem {
  value: string
  label: string
  badge?: number | string
  disabled?: boolean
  icon?: React.ReactNode
  /** Optional data-tour attribute for onboarding coach-marks. */
  dataTour?: string
}

export interface TabsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>, VariantProps<typeof tabsVariants> {
  items: TabItem[]
  defaultValue?: string
  value?: string
  onChange?: (value: string) => void
  orientation?: 'horizontal' | 'vertical'
  size?: 'default' | 'sm' | 'lg'
  listClassName?: string
  triggerClassName?: string
  badgeClassName?: string
}

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      className,
      variant,
      size,
      items,
      defaultValue,
      value,
      onChange,
      orientation = 'horizontal',
      children,
      listClassName,
      triggerClassName,
      badgeClassName,
    },
    ref
  ) => {
    return (
      <ShadcnTabs
        ref={ref}
        defaultValue={defaultValue}
        value={value}
        onValueChange={onChange}
        orientation={orientation}
        className={cn(tabsVariants({ variant, className }))}
      >
        <TabsList className={cn(tabsListVariants({ variant, size }), listClassName)}>
          {items.map(item => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              className={cn(tabsTriggerVariants({ variant, size }), triggerClassName)}
              onClick={() => onChange?.(item.value)}
              {...(item.dataTour ? { 'data-tour': item.dataTour } : {})}
            >
              <div
                className={cn(
                  'relative flex h-full w-fit items-center justify-center gap-2',
                  variant === 'underline' &&
                    'group-data-[state=active]:after:bg-icon-primary px-2 after:absolute after:start-0 after:end-0 after:-bottom-[2px] after:h-[3px] after:rounded-t-sm after:bg-transparent after:transition-all'
                )}
              >
                <div className='flex flex-col items-center gap-0.5'>
                  {item.icon && <div className={cn('flex-shrink-0')}>{item.icon}</div>}
                  <span className='text-center'>{item.label}</span>
                </div>
                {item.badge !== undefined && (
                  <Badge
                    variant='destructive'
                    className={cn('flex h-5 w-5 items-center justify-center rounded-full p-0 text-xs', badgeClassName)}
                  >
                    {item.badge}
                  </Badge>
                )}
              </div>
            </TabsTrigger>
          ))}
        </TabsList>
        {children}
      </ShadcnTabs>
    )
  }
)

Tabs.displayName = 'Tabs'

export { Tabs, TabsContent, TabsList, TabsTrigger, tabsVariants }
